import { useEffect, useMemo, useState } from 'react';

import { useSessionContext } from '~/contexts/SessionContext';
import { lowercaseFirstLetter } from '~/utils/helpers';

import { visitIQFilterFieldConditions } from '../constants';
import { usePeopleFieldsLazyQuery } from '../types/api.graphql';

const useAudienceFilters = () => {
  const { hiddenFieldsIntersection } = useSessionContext();
  const getConditionsFromStorage = () => {
    const conditions = localStorage.getItem(visitIQFilterFieldConditions);
    return conditions ? new Map(JSON.parse(conditions) as Map<string, string>) : undefined;
  };

  const [fieldConditions, setFieldConditions] = useState<Map<string, string>>(getConditionsFromStorage());

  const getFieldCondition = (field: string) => {
    if (loading) return undefined;
    if (!fieldConditions) {
      void loadFilters();
      return undefined;
    }

    return fieldConditions.get(field);
  };

  const [loadFilters, { data, loading }] = usePeopleFieldsLazyQuery();

  useEffect(() => {
    if (data) {
      const mapConditions = new Map<string, string>(
        data?.peopleFields
          ?.filter(
            (item) =>
              !!item.configuration?.stringFilterConfiguration &&
              !hiddenFieldsIntersection.includes(lowercaseFirstLetter(item.filterField)),
          )
          .map((item): [string, string] => [item.filterField, item.configuration.stringFilterConfiguration.condition]),
      );
      setFieldConditions(mapConditions);
      localStorage.setItem(visitIQFilterFieldConditions, JSON.stringify(Array.from(mapConditions.entries())));
    }
  }, [data, hiddenFieldsIntersection]);

  const filters = useMemo(
    () =>
      data?.peopleFields.filter((item) => !hiddenFieldsIntersection.includes(lowercaseFirstLetter(item.filterField))),
    [data?.peopleFields, hiddenFieldsIntersection],
  );

  return { loadFilters, loading, filters, getFieldCondition };
};

export default useAudienceFilters;
